import { get } from "@ember/object"
import { A, pipe } from "@mobily/ts-belt"
import type { FiscalGroup } from "@shared/models/fiscal-group"
import type {
  Group,
  GroupMembership,
  GroupMembershipAccepted,
  GroupMembershipGroup,
  GroupMembershipInvited,
  GroupMembershipPotential,
  GroupMembershipRequested,
  IndividualMembership,
} from "@shared/models/group"
import type { CachedMembership } from "@shared/models/user"
import { filter, includes, isEqual, map } from "lodash-es"

export function isGroupMembershipGroup(
  membership: GroupMembership
): membership is GroupMembershipGroup {
  return (membership as GroupMembership).type === "group-membership-group"
}
export function isGroupMembershipAccepted(
  membership: GroupMembership
): membership is GroupMembershipAccepted {
  return (membership as GroupMembership).type === "group-membership-accepted"
}

export function isGroupMembershipInvited(
  membership: GroupMembership
): membership is GroupMembershipInvited {
  return (membership as GroupMembership).type === "group-membership-invited"
}

export function isGroupMembershipRequested(
  membership: GroupMembership
): membership is GroupMembershipRequested {
  return (membership as GroupMembership).type === "group-membership-requested"
}

export function isGroupMembershipPotential(
  membership: GroupMembership
): membership is GroupMembershipPotential {
  return (membership as GroupMembership).type === "group-membership-potential"
}

export function isGroupMembershipIndividual(
  membership: GroupMembership
): membership is IndividualMembership {
  return (
    (membership as GroupMembership).type === "group-membership-accepted" ||
    (membership as GroupMembership).type === "group-membership-invited"
  )
}

export function membersWithMemberRole(membership) {
  return includes(get(membership.attributes, "roles"), "group:is-member")
}
export function membersWithAdminRole(membership) {
  return includes(get(membership.attributes, "roles"), "group:is-admin")
}

export function pluckSubGroupIds(group: Group) {
  return map(filter(group.attributes.memberships, isGroupMembershipGroup), getGroupId)
}

export function pluckUserIds(group: Group) {
  return map(filter(group.attributes.memberships, isGroupMembershipIndividual), getUserId)
}

export function getGroupId(membership) {
  return get(membership.attributes, "groupId")
}
export function getUserId(membership) {
  return get(membership.attributes, "userId")
}

export function isUpgradedMembership(
  fiscalGroup: FiscalGroup | null,
  membership: GroupMembership
): boolean {
  if (fiscalGroup === null) return false
  if (!(isGroupMembershipAccepted(membership) || isGroupMembershipInvited(membership))) return false

  let id: string | null = null
  if (isGroupMembershipAccepted(membership)) {
    id = get(get(membership, "attributes"), "userId")
  }
  if (isGroupMembershipInvited(membership)) {
    id = get(get(membership, "attributes"), "email")
  }
  if (membership.attributes.roles.includes("group:is-admin")) {
    return true
  } else {
    return includes(get(get(fiscalGroup, "attributes"), "upgradedMemberIds"), id)
  }
}

/**
 * The number of accepted/requested/potential teachers in a locked group.
 */
export function lockedTeacherCount(group: Group): number {
  return pipe(
    group.attributes.memberships,
    A.filter(
      (membership) =>
        isGroupMembershipAccepted(membership) ||
        isGroupMembershipRequested(membership) ||
        isGroupMembershipPotential(membership)
    ),
    A.filter((membership) => isEqual(membership.attributes.roles, ["group:is-member"])),
    A.map((membership) => membership.attributes.userId),
    A.uniq
  ).length
}

export function isCachedMembershipActive(membership: CachedMembership) {
  return (
    membership.state === "open" &&
    membership.membershipType === "group-membership-accepted" &&
    !membership.isAncestor
  )
}

export function isCachedMembershipActiveInclAncestors(membership: CachedMembership) {
  return membership.state === "open" && membership.membershipType === "group-membership-accepted"
}

export function isCachedMembershipActiveTeam(membership: CachedMembership) {
  return isCachedMembershipActive(membership) && membership.usedAs === "team"
}

export function isCachedMembershipActiveSchool(membership: CachedMembership) {
  return isCachedMembershipActive(membership) && membership.usedAs === "school"
}
